/**
 * Product grid + empty state + pagination footer for the live menu.
 * Pure presentation — parent passes the already-resolved visible slice and
 * pagination math.
 */
import { ArrowLeft, ArrowRight } from "lucide-react";
import ProductCard from "../../components/ProductCard";

export default function MenuGrid({
    pageSize,
    error,
    menu,
    visibleProducts,
    visibleCount,
    needsClientFilter,
    pageStart,
    offsetParam,
    hasPrev,
    hasNext,
    onPrev,
    onNext,
    onClearAll,
    storeSlug,
    specialsById,
}) {
    if (error) {
        return (
            <div className="border border-zen-ink/15 p-8 text-center text-zen-ink-soft">
                Live menu temporarily unavailable. Please try again in a moment.
            </div>
        );
    }
    if (menu === null) {
        return (
            <div className="text-center py-16 text-zen-ink-soft">
                Loading live menu from Dutchie…
            </div>
        );
    }

    return (
        <>
            {visibleProducts.length === 0 ? (
                <div className="text-center py-12 border border-zen-ink/10 bg-zen-surface">
                    <p className="font-serif text-2xl text-zen-ink font-light mb-3">
                        No products match your filters.
                    </p>
                    <button
                        onClick={onClearAll}
                        className="text-[11px] uppercase tracking-[0.25em] text-zen-olive border-b border-zen-olive/40 pb-1 hover:border-zen-olive"
                    >
                        Clear filters
                    </button>
                </div>
            ) : (
                <div
                    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2.5"
                    data-testid="product-grid"
                >
                    {visibleProducts.slice(0, pageSize).map((p, i) => (
                        <ProductCard
                            key={`${p.id}-${i}`}
                            product={p}
                            storeSlug={storeSlug}
                            specialsById={specialsById}
                        />
                    ))}
                </div>
            )}

            {/* Pagination — count + prev/next reflect the *filtered* set when
                any client-side filter is active. */}
            <div className="mt-12 flex items-center justify-between border-t border-zen-ink/10 pt-8">
                <button
                    onClick={onPrev}
                    disabled={!hasPrev}
                    data-testid="menu-prev"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    <ArrowLeft size={12} /> Previous
                </button>
                <span
                    className="text-[11px] uppercase tracking-[0.22em] text-zen-muted"
                    data-testid="menu-pagination-count"
                >
                    {visibleProducts.length > 0 &&
                        (needsClientFilter
                            ? `${pageStart + 1}–${pageStart + visibleProducts.length} of ${visibleCount.toLocaleString()}`
                            : `${offsetParam + 1}–${offsetParam + visibleProducts.length} of ${visibleCount.toLocaleString()}`)}
                </span>
                <button
                    onClick={onNext}
                    disabled={!hasNext}
                    data-testid="menu-next"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    Next <ArrowRight size={12} />
                </button>
            </div>
        </>
    );
}
